/**
 * NextActionCard — DRR-309 Phase 2
 *
 * Prominent "What's Next" call-to-action card.
 * Shows the priority-determined next action with deadline and regulatory context.
 */

import React from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Button,
  Badge,
  ThemeIcon,
  Alert,
  Paper,
} from '@mantine/core';
import {
  IconArrowRight,
  IconClock,
  IconAlertTriangle,
  IconCheck,
  IconLock,
  IconScale,
} from '@tabler/icons-react';
import type { NextActionCardProps, NextAction } from '../types';

const PRIORITY_CONFIG: Record<
  NextAction['priority'],
  { color: string; variant: 'filled' | 'light' | 'outline' }
> = {
  critical: { color: 'red', variant: 'filled' },
  high: { color: 'orange', variant: 'filled' },
  medium: { color: 'blue', variant: 'light' },
  low: { color: 'gray', variant: 'light' },
};

export function NextActionCard({
  action,
  onAction,
  compact = false,
}: NextActionCardProps) {
  const priorityConfig = PRIORITY_CONFIG[action.priority];
  const isCritical = action.priority === 'critical' || action.priority === 'high';

  if (compact) {
    return (
      <Group gap="xs" wrap="nowrap">
        <Badge
          size="sm"
          color={priorityConfig.color}
          variant={priorityConfig.variant}
          leftSection={
            action.isBlocked ? <IconLock size={10} /> : <IconArrowRight size={10} />
          }
        >
          {action.title}
        </Badge>
        {action.deadlineLabel && (
          <Text size="xs" c={isCritical ? 'red' : 'dimmed'}>
            {action.deadlineLabel}
          </Text>
        )}
      </Group>
    );
  }

  // Blocked state
  if (action.isBlocked) {
    return (
      <Alert
        icon={<IconLock size={18} />}
        color="yellow"
        variant="light"
        title="Waiting for Prerequisites"
      >
        <Stack gap="xs">
          <Text size="sm">
            <strong>{action.title}</strong> is not yet available.
          </Text>
          {action.blockedReason && (
            <Text size="xs" c="dimmed">
              {action.blockedReason}
            </Text>
          )}
        </Stack>
      </Alert>
    );
  }

  return (
    <Card
      padding="lg"
      withBorder
      style={{
        borderColor:
          isCritical ? `var(--mantine-color-${priorityConfig.color}-5)` : undefined,
        borderWidth: isCritical ? 2 : 1,
        backgroundColor: isCritical
          ? `var(--mantine-color-${priorityConfig.color}-0)`
          : undefined,
      }}
    >
      <Stack gap="md">
        {/* Header with priority badge */}
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon
              size="lg"
              variant={priorityConfig.variant}
              color={priorityConfig.color}
            >
              {isCritical ? <IconAlertTriangle size={20} /> : <IconArrowRight size={20} />}
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                {isCritical ? 'Urgent' : 'Next Step'}
              </Text>
              <Text size="lg" fw={700}>
                {action.title}
              </Text>
            </div>
          </Group>

          {action.deadlineLabel && (
            <Badge
              size="lg"
              color={isCritical ? 'red' : 'blue'}
              variant={isCritical ? 'filled' : 'light'}
              leftSection={<IconClock size={14} />}
            >
              {action.deadlineLabel}
            </Badge>
          )}
        </Group>

        {/* Description */}
        {action.description && (
          <Text size="sm" c="dimmed">
            {action.description}
          </Text>
        )}

        {/* Regulatory citation */}
        {action.regulatoryCitation && (
          <Paper p="xs" bg="violet.0" radius="sm">
            <Group gap="xs">
              <ThemeIcon size="xs" variant="light" color="violet">
                <IconScale size={10} />
              </ThemeIcon>
              <Text size="xs" c="violet.7">
                {action.regulatoryCitation}
              </Text>
            </Group>
          </Paper>
        )}

        {/* Action button */}
        <Button
          size="lg"
          color={priorityConfig.color}
          variant={priorityConfig.variant === 'filled' ? 'filled' : 'light'}
          fullWidth
          rightSection={<IconArrowRight size={18} />}
          onClick={onAction}
        >
          {action.type === 'transition' ? 'Continue' : 'Start'}
        </Button>
      </Stack>
    </Card>
  );
}

export default NextActionCard;
